/*
 * Copyright (c) 2024 SARL SIGMA POLYNESIA
 */

import { useCallback, useMemo } from 'react'
import { OffsetPagination } from '@jalik/pagination-helper'

type UsePaginationOptions = {
  limit?: number
  maxLimit?: number
  onChange (page: number, limit: number): void
  page?: number
  totalElements: number
}

type State = {
  hasNext: boolean,
  hasPrevious: boolean,
  limit: number,
  nextPage: number,
  offset: number,
  page: number,
  previousPage: number,
  totalElements: number,
  totalPages: number,
}

export type UsePaginationHook = State & {
  change (page: number, limit: number): void;
  first (): void;
  last (): void;
  next (): void;
  previous (): void;
  setLimit (limit: number): void;
  setPage (page: number): void;
}

function usePagination (options: UsePaginationOptions): UsePaginationHook {
  const {
    maxLimit,
    limit: initialLimit = 10,
    page: initialPage = 1,
    onChange,
    totalElements = 0
  } = options

  const state: State = useMemo<State>(() => {
    const p = new OffsetPagination({
      maxLimit,
      limit: initialLimit,
      page: initialPage,
      totalElements
    })

    const limit = p.getLimit()
    const totalPages = p.getTotalPages()
    const page = p.getPage()
    const offset = p.getOffset()
    const nextPage = p.getNextPage()
    const previousPage = p.getPreviousPage()
    const hasNext = p.hasNext()
    const hasPrevious = p.hasPrevious()

    return {
      hasNext,
      hasPrevious,
      limit,
      nextPage,
      offset,
      page,
      previousPage,
      totalElements,
      totalPages
    }
  }, [initialLimit, initialPage, maxLimit, totalElements])

  const first = useCallback(() => {
    onChange(1, state.limit)
  }, [onChange, state.limit])

  const last = useCallback(() => {
    onChange(state.totalPages, state.limit)
  }, [onChange, state.limit, state.totalPages])

  const next = useCallback(() => {
    onChange(state.nextPage, state.limit)
  }, [onChange, state.limit, state.nextPage])

  const previous = useCallback(() => {
    onChange(state.previousPage, state.limit)
  }, [onChange, state.limit, state.previousPage])

  const setLimit = useCallback((value: number) => {
    onChange(state.page, value)
  }, [onChange, state.page])

  const setPage = useCallback((value: number) => {
    onChange(value, state.limit)
  }, [onChange, state.limit])

  const change = useCallback((page: number, limit: number) => {
    onChange(page, limit)
  }, [onChange])

  return useMemo(() => ({
    ...state,
    change,
    first,
    previous,
    next,
    last,
    setLimit,
    setPage
  }), [change, first, last, next, previous, setLimit, setPage, state])
}

export function handlePaginationChange (setter: (s: { [key: string]: any }) => any) {
  return (page: number, limit: number) => {
    setter((s: { [key: string]: any }) => ({ ...s, limit, page }))
  }
}

export default usePagination
